import { useState, type KeyboardEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquareQuote, Plus, X } from 'lucide-react';
import {
  DEPARTMENT_PHRASES_PER_FIELD,
  DEPARTMENT_PHRASE_MAX,
  QUICK_PICK_FIELDS,
  cleanDepartmentPhrase,
  cleanDepartmentPhrases,
  type DepartmentPhrasesResponse,
  type PhraseLists,
  type QuickPickField,
} from '@his/shared';
import { get, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, FormSkeleton, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { EMR_FORMS_SETTINGS_PATH, departmentPhrasesKey } from './DepartmentPhrasesCard';

type Draft = Record<QuickPickField, string[]>;

export type PhraseRefusal = 'dept_phrase_empty' | 'dept_phrase_too_long' | 'dept_phrase_duplicate' | 'dept_phrase_full';

export function draftOf(phrases: PhraseLists): Draft {
  return Object.fromEntries(QUICK_PICK_FIELDS.map((field) => [field, [...(phrases[field] ?? [])]])) as Draft;
}

export function addPhrase(list: readonly string[], raw: string): { list: string[]; refusal: PhraseRefusal | null } {
  if (raw.trim().length > DEPARTMENT_PHRASE_MAX) return { list: [...list], refusal: 'dept_phrase_too_long' };
  const phrase = cleanDepartmentPhrase(raw);
  if (!phrase) return { list: [...list], refusal: 'dept_phrase_empty' };
  if (list.some((existing) => existing.toLocaleLowerCase() === phrase.toLocaleLowerCase())) return { list: [...list], refusal: 'dept_phrase_duplicate' };
  if (list.length >= DEPARTMENT_PHRASES_PER_FIELD) return { list: [...list], refusal: 'dept_phrase_full' };
  return { list: [...list, phrase], refusal: null };
}

function fieldLabel(field: QuickPickField): string {
  return tr('admin', `qp_field_${field}`);
}

function PhraseListSection({
  field,
  phrases,
  editable,
  onChange,
}: {
  field: QuickPickField;
  phrases: string[];
  editable: boolean;
  onChange: (phrases: string[]) => void;
}) {
  const [text, setText] = useState('');
  const [refusal, setRefusal] = useState<PhraseRefusal | null>(null);
  const label = fieldLabel(field);

  const add = () => {
    const result = addPhrase(phrases, text);
    setRefusal(result.refusal);
    if (result.refusal) return;
    onChange(result.list);
    setText('');
  };

  const onKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    add();
  };

  return (
    <FormSection title={label} description={tr('admin', 'dept_phrases_used', { count: phrases.length, max: DEPARTMENT_PHRASES_PER_FIELD })} columns={1}>
      {phrases.length === 0 ? (
        <p className="text-sm text-muted-foreground">{tr('admin', 'dept_phrases_none')}</p>
      ) : (
        <ul className="flex flex-wrap gap-1.5" aria-label={label}>
          {phrases.map((phrase) => (
            <li key={phrase} className="inline-flex max-w-full items-center gap-1 rounded-full border border-info/30 bg-info/[0.06] py-0.5 pe-1 ps-2.5 text-xs text-info">
              <span className="min-w-0 [overflow-wrap:anywhere]">{phrase}</span>
              {editable && (
                <button
                  type="button"
                  className="grid h-5 w-5 shrink-0 place-items-center rounded-full hover:bg-info/15 focus-ring"
                  aria-label={tr('admin', 'dept_phrases_remove', { phrase })}
                  onClick={() => onChange(phrases.filter((existing) => existing !== phrase))}
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <div className="space-y-1">
          <div className="flex gap-2">
            <Input
              value={text}
              maxLength={DEPARTMENT_PHRASE_MAX}
              placeholder={tr('admin', 'dept_phrases_add_placeholder')}
              aria-label={tr('admin', 'dept_phrases_add_to', { field: label })}
              aria-invalid={refusal ? true : undefined}
              onChange={(event) => {
                setText(event.target.value);
                if (refusal) setRefusal(null);
              }}
              onKeyDown={onKey}
            />
            <Button type="button" variant="outline" onClick={add} disabled={phrases.length >= DEPARTMENT_PHRASES_PER_FIELD}>
              <Plus className="h-4 w-4" aria-hidden />
              {tr('admin', 'dept_phrases_add')}
            </Button>
          </div>
          {refusal && (
            <p className="text-xs text-destructive" role="alert">
              {tr('admin', refusal, { max: refusal === 'dept_phrase_too_long' ? DEPARTMENT_PHRASE_MAX : DEPARTMENT_PHRASES_PER_FIELD })}
            </p>
          )}
        </div>
      )}
    </FormSection>
  );
}

function DepartmentPhrasesEditor({ initial, returnTo }: { initial: DepartmentPhrasesResponse; returnTo: string }) {
  const { can, canWrite } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editable = canWrite && can('emr.config.manage', 'tenant.settings.manage');
  const [draft, setDraft] = useState<Draft>(() => draftOf(initial.phrases));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => put<DepartmentPhrasesResponse>(`/emr/department-phrases/${initial.departmentId}`, { phrases: cleanDepartmentPhrases(draft) }),
    onSuccess: (response) => {
      queryClient.setQueryData(departmentPhrasesKey(initial.departmentId), response);
      void queryClient.invalidateQueries({ queryKey: ['quick-picks'] });
      toast.success(tr('admin', 'dept_phrases_saved'));
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = !save.isSuccess && JSON.stringify(cleanDepartmentPhrases(draft)) !== JSON.stringify(cleanDepartmentPhrases(initial.phrases));

  return (
    <FormScreen
      title={tr('admin', 'dept_phrases_title')}
      description={tr('admin', 'dept_phrases_screen_hint', { department: initial.departmentName })}
      eyebrow={initial.departmentName}
      icon={MessageSquareQuote}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        if (editable && dirty) save.mutate();
      }}
    >
      {!editable && <Alert>{tr('admin', 'you_can_view_these_settings_but_not_change_them')}</Alert>}
      {QUICK_PICK_FIELDS.map((field) => (
        <PhraseListSection
          key={field}
          field={field}
          phrases={draft[field]}
          editable={editable}
          onChange={(phrases) => setDraft((current) => ({ ...current, [field]: phrases }))}
        />
      ))}
      {error && <Alert variant="destructive">{error}</Alert>}
      {editable && <FormActions submitLabel={tr('admin', 'dept_phrases_save')} loading={save.isPending} disabled={!dirty} cancelTo={returnTo} />}
    </FormScreen>
  );
}

export function DepartmentPhrasesPage() {
  const { departmentId = '' } = useParams<{ departmentId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), EMR_FORMS_SETTINGS_PATH);
  const phrases = useQuery({
    queryKey: departmentPhrasesKey(departmentId),
    queryFn: () => get<DepartmentPhrasesResponse>(`/emr/department-phrases/${departmentId}`),
    enabled: Boolean(departmentId),
    staleTime: 60_000,
  });

  if (phrases.isLoading) return <FormSkeleton />;
  if (!phrases.data) return <Alert variant="destructive">{phrases.error ? describeError(phrases.error) : tr('admin', 'dept_phrases_not_found')}</Alert>;
  return <DepartmentPhrasesEditor key={phrases.data.departmentId} initial={phrases.data} returnTo={returnTo} />;
}
